import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    People as PeopleIcon,
    School as SchoolIcon,
    Class as ClassIcon,
    TrendingUp as TrendingUpIcon,
    AccountBalance as AccountBalanceIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Person as PersonIcon,
    Group as GroupIcon,
    EventBusy as EventBusyIcon,
    Star as StarIcon,
    Percent as PercentIcon,
} from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import RTL from '../../../../utils/RTL';
import StandardTable from '../../../common/table/StandardTable';
import StatCard from '../../../common/StatCard';
import FinancialOverviewCards from '../../../common/FinancialOverviewCards';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { API } from '../../../../utils/apiRoutes';
import PersianDatePicker from '../../../common/PersianDatePicker';
import { PRESET_OPTIONS, calculateDatesFromPreset, toApiDateString } from '../../../../utils/dateRangePresets';

function SystemStatistics() {
    const DEFAULT_PRESET = 'this_month';
    const initialDates = useMemo(() => calculateDatesFromPreset(DEFAULT_PRESET), []);
    const [preset, setPreset] = useState(DEFAULT_PRESET);
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);

    // Update dates when preset changes
    const handlePresetChange = (event) => {
        const newPreset = event.target.value;
        setPreset(newPreset);
        const { start, end } = calculateDatesFromPreset(newPreset);
        setStartDate(start);
        setEndDate(end);
    };

    // Build API URL with query params. Use local-date formatting (not UTC
    // toISOString) so the same preset yields the same start/end as گزارش مالی
    // (RevenueOverview); otherwise the +03:30 offset shifts the day and the two
    // screens compute different figures from the shared endpoint.
    const apiParams = useMemo(() => {
        const params = {};
        const start = toApiDateString(startDate);
        const end = toApiDateString(endDate);
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        return params;
    }, [startDate, endDate]);

    // First, fetch quick statistics (users, courses, enrollments only) for faster initial load
    const { data: quickData, loading: quickLoading, error: quickError } = useFetch(API.quickSystemStatistics, { params: apiParams });

    // Then, fetch enhanced statistics (all other sections)
    const { data: enhancedData, loading: enhancedLoading, error: enhancedError } = useFetch(API.enhancedSystemStatistics, { params: apiParams });

    // Financial section is rendered from the *same* endpoint as گزارش مالی
    // (RevenueOverview) so the two screens are always identical.
    const { data: financialOverview } = useFetch(API.revenueOverview, { params: apiParams });

    // Merge data: use quick data for basic sections, enhanced data for advanced sections
    const data = useMemo(() => {
        if (!quickData && !enhancedData) return null;

        return {
            // Basic sections: quick stats paint first, enhanced stats fill in any extra
            // fields (e.g. course type breakdown lives only in the enhanced response).
            // Merge field-by-field so the complete enhanced object isn't shadowed.
            users: { ...quickData?.users, ...enhancedData?.users },
            courses: { ...quickData?.courses, ...enhancedData?.courses },
            enrollments: { ...quickData?.enrollments, ...enhancedData?.enrollments },
            // Advanced sections from enhanced statistics
            attendance: enhancedData?.attendance,
            financial: enhancedData?.financial,
            retention: enhancedData?.retention,
            teacher_performance: enhancedData?.teacher_performance,
            period: enhancedData?.period,
        };
    }, [quickData, enhancedData]);

    // Loading state: show loading if either is loading
    const loading = quickLoading || enhancedLoading;

    // Error state: show error if both fail
    const error = quickError && enhancedError ? enhancedError : null;

    const handleStartDateChange = (date) => {
        if (date && !isNaN(date.getTime())) {
            setStartDate(date);
            // When user manually changes date, set preset to 'custom'
            setPreset('custom');
        }
    };

    const handleEndDateChange = (date) => {
        if (date && !isNaN(date.getTime())) {
            setEndDate(date);
            // When user manually changes date, set preset to 'custom'
            setPreset('custom');
        }
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    // Cohort retention chip. `rate` is null until at least one enrollment is
    // old enough for the horizon; cohorts under 10 matured students render
    // grey (mirrors the backend low_data threshold) — too noisy to rank on.
    const renderCohortRetention = (rate, cohortSize) => {
        if (rate === null || rate === undefined) {
            return (
                <Typography variant="caption" color="text.secondary">داده ناکافی</Typography>
            );
        }
        return (
            <Chip
                label={`${toPersianDigits(Math.round(rate))}% (${toPersianDigits(cohortSize || 0)})`}
                color={(cohortSize || 0) < 10 ? 'default' : rate >= 80 ? 'success' : rate >= 60 ? 'warning' : 'error'}
                size="small"
            />
        );
    };

    const attendanceRate = () => {
        const rate = data?.attendance?.attendance_rate;
        if (rate !== undefined && rate !== null && rate !== '' && !isNaN(Number(rate))) {
            return `${toPersianDigits(Math.round(Number(rate)))}%`;
        }
        return '۰٪';
    };

    // A spinner sized for an enhanced section that is still loading after quick data arrived.
    const sectionLoader = (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
        </Box>
    );

    // Show error only if we don't have data
    if (error && !data) {
        return (
            <RTL>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </RTL>
        );
    }

    // Show info only if we don't have data and not loading
    if (!data && !loading) {
        return (
            <RTL>
                <Box sx={{ p: 3 }}>
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>داده‌ای برای نمایش وجود ندارد.</Alert>
                </Box>
            </RTL>
        );
    }

    // Initial load: a single page-level spinner instead of one per section.
    if (!data) {
        return (
            <RTL>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            </RTL>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3, position: 'relative' }}>
                {/* Error Alert */}
                {error && data && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Refresh indicator — shown only when reloading over existing data */}
                {loading && data && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 16,
                            right: 16,
                            zIndex: 9999,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            p: 1.5,
                            boxShadow: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <CircularProgress size={24} />
                        <Typography variant="body2">در حال به‌روزرسانی...</Typography>
                    </Box>
                )}

                <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                    آمار سامانه
                </Typography>

                {/* Date Filters — sticky so the controls stay reachable while scrolling */}
                <Paper
                    sx={{
                        p: 2,
                        mb: 3,
                        position: 'sticky',
                        // The dashboard scroll container (Dashboard.jsx) pads 1rem on top;
                        // pull the bar up by that amount so it pins flush to the viewport
                        // with no scrolled content peeking through above it.
                        top: '-1rem',
                        // Must sit above StandardTable's sticky header cells (zIndex 3),
                        // otherwise the section tables' headers scroll up over this bar.
                        zIndex: 4,
                        bgcolor: 'background.default',
                        borderColor: 'border.main',
                    }}
                >
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{
                            // Neutralize the global MuiTextField margin so the date pickers
                            // line up with the (margin-less) period Select.
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>دوره زمانی</InputLabel>
                                <Select
                                    value={preset}
                                    label="دوره زمانی"
                                    onChange={handlePresetChange}
                                >
                                    {PRESET_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="custom">انتخاب دستی</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={startDate ? new Date(startDate) : null}
                                onChange={handleStartDateChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={endDate ? new Date(endDate) : null}
                                onChange={handleEndDateChange}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* User Statistics */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ ml: 1, color: 'primary.main' }} />
                        آمار کاربران
                    </Typography>
                    {data?.users ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={PeopleIcon}
                                    label="کل هنرجویان"
                                    value={toPersianDigits(data.users?.total_students || 0)}
                                    hint={`فعال: ${toPersianDigits(data.users?.active_students || 0)}`}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={SchoolIcon}
                                    label="کل آموزگاران"
                                    value={toPersianDigits(data.users?.total_teachers || 0)}
                                    hint={`فعال: ${toPersianDigits(data.users?.active_teachers || 0)}`}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={GroupIcon}
                                    label="کارمندان"
                                    value={toPersianDigits(data.users?.total_staff || 0)}
                                />
                            </Grid>
                        </Grid>
                    ) : sectionLoader}
                </Paper>

                {/* Course Statistics */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <ClassIcon sx={{ ml: 1, color: 'primary.main' }} />
                        آمار دوره‌ها و کلاس‌ها
                    </Typography>
                    {data?.courses ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={ClassIcon}
                                    label="کل دوره‌ها"
                                    value={toPersianDigits(data.courses?.total_courses || 0)}
                                    hint={`فعال: ${toPersianDigits(data.courses?.active_courses || 0)}`}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={PersonIcon}
                                    label="دوره‌های خصوصی"
                                    value={toPersianDigits(data.courses?.individual_courses || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={GroupIcon}
                                    label="دوره‌های گروهی"
                                    value={toPersianDigits(data.courses?.group_courses || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={CheckCircleIcon}
                                    label="کلاس‌های تکمیل ظرفیت"
                                    value={toPersianDigits(data.courses?.full_capacity_courses || 0)}
                                />
                            </Grid>
                        </Grid>
                    ) : sectionLoader}
                </Paper>

                {/* Enrollment Statistics */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ ml: 1, color: 'primary.main' }} />
                        آمار ثبت‌نام‌ در دوره ها
                    </Typography>
                    {data?.enrollments ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={TrendingUpIcon}
                                    label="ثبت‌نام‌های جدید در دوره‌ها"
                                    value={toPersianDigits(data.enrollments?.total_enrollments || 0)}
                                    hint={`فعال (بایگانی‌نشده): ${toPersianDigits(data.enrollments?.active_enrollments || 0)}`}
                                />
                            </Grid>
                        </Grid>
                    ) : sectionLoader}
                </Paper>

                {/* Attendance Statistics */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ ml: 1, color: 'primary.main' }} />
                        آمار حضور و غیاب
                    </Typography>
                    {data?.attendance ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={CheckCircleIcon}
                                    color="success"
                                    label="تعداد حضور"
                                    value={toPersianDigits(data.attendance?.total_present || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={CancelIcon}
                                    color="error"
                                    label="تعداد غیبت"
                                    value={toPersianDigits(data.attendance?.total_absent || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={EventBusyIcon}
                                    color="warning"
                                    label="تعداد مرخصی"
                                    value={toPersianDigits(data.attendance?.total_vacation || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={CancelIcon}
                                    color="neutral"
                                    label="تعداد کنسل"
                                    value={toPersianDigits(data.attendance?.total_canceled || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={StarIcon}
                                    color="info"
                                    label="موارد ویژه"
                                    value={toPersianDigits(data.attendance?.total_special || 0)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <StatCard
                                    icon={PercentIcon}
                                    label="نرخ حضور"
                                    value={attendanceRate()}
                                />
                            </Grid>
                        </Grid>
                    ) : sectionLoader}
                </Paper>

                {/* Financial Statistics */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <AccountBalanceIcon sx={{ ml: 1, color: 'primary.main' }} />
                        آمار مالی
                    </Typography>
                    {financialOverview ? (
                        <FinancialOverviewCards data={financialOverview} />
                    ) : sectionLoader}
                </Paper>

                {/* Retention Rate Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ ml: 1, color: 'primary.main' }} />
                        نرخ نگهداری هنرجو
                    </Typography>
                    {data?.retention ? (
                        <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                            <strong>توضیح:</strong> <strong>نگهداری ۶ماهه / ۱۲ماهه</strong> یعنی از هنرجویانی که دست‌کم ۶ (یا ۱۲) ماه از ثبت‌نامشان نزد این آموزگار گذشته، چند درصد در پایان آن بازه هنوز نزد او فعال بوده‌اند.
                            این سنجه‌ها برخلاف «نرخ کل دوران» به سابقهٔ آموزگار وابسته نیستند و آموزگاران قدیمی و جدید را با معیار یکسان می‌سنجند؛ عدد داخل پرانتز اندازهٔ گروه سنجش است و گروه‌های کوچک‌تر از ۱۰ نفر خاکستری نمایش داده می‌شوند چون هنوز قابل اتکا نیستند.
                            <strong> میانگین ماندگاری</strong> برآوردی است از این‌که یک هنرجو به‌طور متوسط چند ماه نزد این آموزگار می‌ماند.
                            هنرجویانی که از این آموزگار جدا شده ولی در دوره‌ای دیگر فعال‌اند، در ستون «فعال در دوره‌های دیگر» شمرده می‌شوند. (این محاسبات مستقل از بازهٔ زمانی انتخاب‌شده‌اند.)
                        </Typography>
                        <StandardTable
                            maxHeight={580}
                            getRowKey={(t) => t.teacher_id}
                            rows={data.retention.teacher_retention || []}
                            columns={[
                                { id: 'teacher_name', label: 'معلم', align: 'start', render: (t) => t.teacher_name },
                                { id: 'total_students', label: 'کل\nهنرجویان', render: (t) => toPersianDigits(t.total_students) },
                                { id: 'active_students', label: 'فعال', compact: true, render: (t) => toPersianDigits(t.active_students) },
                                { id: 'completed_and_active_elsewhere', label: 'فعال در\nدوره‌های دیگر', priority: 'secondary', render: (t) => toPersianDigits(t.completed_and_active_elsewhere || 0) },
                                { id: 'inactive_students', label: 'غیرفعال', compact: true, priority: 'secondary', render: (t) => toPersianDigits(t.inactive_students) },
                                { id: 'retention_6m', label: 'نگهداری\n۶ماهه', render: (t) => renderCohortRetention(t.retention_6m, t.cohort_6m) },
                                { id: 'retention_12m', label: 'نگهداری\n۱۲ماهه', render: (t) => renderCohortRetention(t.retention_12m, t.cohort_12m) },
                                { id: 'avg_lifetime_months', label: 'میانگین\nماندگاری', render: (t) => (
                                    t.avg_lifetime_months === null || t.avg_lifetime_months === undefined
                                        ? '—'
                                        : `${toPersianDigits(t.avg_lifetime_months)} ماه`
                                ) },
                                { id: 'retention_rate', label: 'نرخ کل\nدوران', priority: 'secondary', render: (t) => (
                                    <Chip
                                        label={`${toPersianDigits(Math.round(t.retention_rate))}%`}
                                        color={t.retention_rate >= 80 ? 'success' : t.retention_rate >= 60 ? 'warning' : 'error'}
                                        size="small"
                                    />
                                ) },
                            ]}
                        />
                        </>
                    ) : sectionLoader}
                </Paper>

                {/* Teacher Performance Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <SchoolIcon sx={{ ml: 1, color: 'primary.main' }} />
                        عملکرد معلمان
                    </Typography>
                    {data?.teacher_performance !== undefined ? (
                        data.teacher_performance && data.teacher_performance.length > 0 ? (
                            <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                                <strong>توضیح:</strong> همهٔ ستون‌ها بر اساس بازهٔ زمانی انتخاب‌شده محاسبه می‌شوند؛ «تعداد هنرجو» و «تعداد دوره» یعنی هنرجویان و دوره‌هایی که در این بازه فعالیت داشته‌اند (شامل ثبت‌نام‌های بایگانی‌شده).
                                نرخ حضور = (حاضر + غایب) / حاضر ؛ «درآمد» سهم آموزشگاه از عملیات این آموزگار در بازه است.
                            </Typography>
                            <StandardTable
                                getRowKey={(t) => t.teacher_id}
                                rows={data.teacher_performance || []}
                                columns={[
                                    { id: 'teacher_name', label: 'معلم', align: 'start', render: (t) => t.teacher_name },
                                    { id: 'student_count', label: 'تعداد\nهنرجو', render: (t) => toPersianDigits(t.student_count) },
                                    { id: 'courses_count', label: 'تعداد\nدوره', priority: 'secondary', render: (t) => toPersianDigits(t.courses_count) },
                                    { id: 'attendance_rate', label: 'نرخ\nحضور', render: (t) => (
                                        <Chip
                                            label={`${toPersianDigits(Math.round(t.attendance_stats?.attendance_rate || 0))}%`}
                                            color={t.attendance_stats?.attendance_rate >= 80 ? 'success' : t.attendance_stats?.attendance_rate >= 60 ? 'warning' : 'error'}
                                            size="small"
                                        />
                                    ) },
                                    { id: 'present', label: 'حضور', compact: true, priority: 'secondary', render: (t) => toPersianDigits(t.attendance_stats?.present || 0) },
                                    { id: 'absent', label: 'غایب', compact: true, priority: 'secondary', render: (t) => toPersianDigits(t.attendance_stats?.absent || 0) },
                                    { id: 'vacation', label: 'مرخصی', compact: true, priority: 'secondary', render: (t) => toPersianDigits(t.attendance_stats?.vacation || 0) },
                                    { id: 'total_revenue', label: 'درآمد', compact: true, render: (t) => `${formatCurrency(t.total_revenue)} تومان` },
                                ]}
                            />
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                داده‌ای برای نمایش وجود ندارد.
                            </Typography>
                        )
                    ) : sectionLoader}
                </Paper>

            </Box>
        </RTL>
    );
}

export default SystemStatistics;
